import React, { useState, useEffect } from "react";
import { Box, Divider, Switch, TextField, Typography } from "@mui/material";
import { DragIndicator as DragHandleIcon } from "@mui/icons-material";
import {
  SettingKey,
  TableColumnConfigDto,
  TableConfigDto,
} from "@swagger/typescript-fetch-goflyer";

// Column keys for each table setting, in default order
export const TABLE_COLUMN_KEYS: Partial<Record<SettingKey, string[]>> = {
  [SettingKey.TableFlyerItems]: [
    "edit",
    "gfImages",
    "name",
    "nameChinese",
    "rank",
    "quantity",
    "salePrice",
    "regularPriceString",
    "topSale",
    "showAt",
    "gfFlyerItemCategory.name",
    "unit",
    "priceRemarks",
    "limitString",
    "promotionTag",
    "flyerItemMetaData",
    "itemSource",
    "createdAt",
    "updatedAt",
    "CreatedBy",
    "goflyer_website",
    "store_website",
    "id",
  ],
  [SettingKey.TableFlyers]: [
    "edit",
    "pdf",
    "merchant",
    "flyerTag",
    "validStartDate",
    "validEndDate",
    "updatedAt",
    "createdAt",
    "CreatedBy",
    "viewFlyer",
    "autoUploadedSource",
    "sourceImageCount",
    "generateCoordinates",
    "jobTestingRecord",
    "aiGenerationStatus",
    "aiGenerationModel",
    "aiGenerationEffort",
    "aiGenerationErrorLog",
    "reviewStatus",
    "retryCoordinates",
    "id",
  ],
  [SettingKey.TableMerchants]: [
    "edit",
    "gfImages",
    "name",
    "domain",
    "aiCoordinationMode",
    "updatedAt",
    "createdAt",
    "id",
  ],
  [SettingKey.TableStores]: [
    "edit",
    "gfImages",
    "merchant",
    "signature",
    "longitude",
    "latitude",
    "phone",
    "openTime",
    "closeTime",
    "storeTag",
    "city",
    "province",
    "zip",
    "country",
    "slug",
    "updatedAt",
    "createdAt",
    "id",
  ],
  [SettingKey.TableFlyerItemLists]: [
    "edit",
    "copy",
    "merchant",
    "flyers",
    "validStartDate",
    "validEndDate",
    "updatedAt",
    "createdAt",
    "CreatedBy",
    "aiGenerationStatus",
    "aiGenerationModel",
    "aiGenerationEffort",
    "aiGenerationErrorLog",
    "reviewStatus",
    "retryCoordinates",
  ],
};

export const TABLE_SETTING_KEYS = new Set<SettingKey>([
  SettingKey.TableFlyerItems,
  SettingKey.TableFlyerItemLists,
  SettingKey.TableFlyers,
  SettingKey.TableMerchants,
  SettingKey.TableStores,
]);

const parseConfig = (
  editValue: string,
  knownKeys: string[],
): TableConfigDto => {
  try {
    const parsed = JSON.parse(editValue) as Partial<TableConfigDto>;
    const existingKeys = new Set((parsed.columns ?? []).map((c) => c.key));
    const merged: TableColumnConfigDto[] = [
      ...(parsed.columns ?? []),
      ...knownKeys
        .filter((k) => !existingKeys.has(k))
        .map((k, i) => ({
          key: k,
          visible: true,
          order: (parsed.columns?.length ?? 0) + i,
        })),
    ].sort((a, b) => a.order - b.order);
    return { columns: merged, pageLimit: parsed.pageLimit ?? 50 };
  } catch {
    return {
      columns: knownKeys.map((k, i) => ({ key: k, visible: true, order: i })),
      pageLimit: 50,
    };
  }
};

const formatKey = (key: string) =>
  key
    .replace(/\./g, " › ")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

interface TableConfigEditorProps {
  settingKey: SettingKey;
  editValue: string;
  onEditValueChange: (v: string) => void;
  testIdPrefix?: string;
}

export const TableConfigEditor = ({
  settingKey,
  editValue,
  onEditValueChange,
  testIdPrefix = "admin-user-settings-table-config",
}: TableConfigEditorProps) => {
  const knownKeys = TABLE_COLUMN_KEYS[settingKey] ?? [];
  const [config, setConfig] = useState<TableConfigDto>(() =>
    parseConfig(editValue, knownKeys),
  );

  useEffect(() => {
    onEditValueChange(JSON.stringify(config));
  }, []);

  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);

  const update = (next: TableConfigDto) => {
    const withOrder: TableConfigDto = {
      ...next,
      columns: next.columns.map((c, i) => ({ ...c, order: i })),
    };
    setConfig(withOrder);
    onEditValueChange(JSON.stringify(withOrder));
  };

  const toggleVisible = (idx: number) => {
    const cols = config.columns.map((c, i) =>
      i === idx ? { ...c, visible: !c.visible } : c,
    );
    update({ ...config, columns: cols });
  };

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${idx}`);
    setDraggedIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIdx) return;

    const cols = [...config.columns];
    const [moved] = cols.splice(draggedIdx, 1);
    cols.splice(targetIdx, 0, moved);

    update({ ...config, columns: cols });
    setDraggedIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  const handlePageLimit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    update({ ...config, pageLimit: isNaN(val) ? 50 : val });
  };

  return (
    <Box
      data-testid={`${testIdPrefix}-container`}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: "#fafafa",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#374151", minWidth: 90 }}
        >
          Page Limit
        </Typography>
        <TextField
          data-testid={`${testIdPrefix}-page-limit`}
          size="small"
          type="number"
          value={config.pageLimit}
          onChange={handlePageLimit}
          onWheel={(e) => (e.target as HTMLElement).blur()}
          inputProps={{ min: 1, max: 100 }}
          sx={{
            width: 100,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "&.Mui-focused fieldset": {
                borderColor: "#f58220",
              },
            },
          }}
        />
      </Box>

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Columns ({config.columns.length})
        </Typography>
        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
          Visible / Order
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
        {config.columns.map((col, idx) => (
          <React.Fragment key={col.key}>
            {idx > 0 && <Divider />}
            <Box
              data-testid={`${testIdPrefix}-row-${idx}`}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              sx={{
                px: 2,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: col.visible ? "white" : "#f9fafb",
                transition: "background 0.15s, opacity 0.15s",
                opacity: draggedIdx === idx ? 0.4 : 1,
                border:
                  draggedIdx === idx
                    ? "1px dashed #9ca3af"
                    : "1px solid transparent",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 0.5,
                  cursor: "grab",
                  color: "#9ca3af",
                  "&:hover": { color: "#6b7280" },
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <DragHandleIcon sx={{ fontSize: 20 }} />
              </Box>

              <Box
                sx={{
                  minWidth: 24,
                  height: 24,
                  borderRadius: "6px",
                  bgcolor: "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "#9ca3af", fontSize: "11px" }}
                >
                  {idx}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  color: col.visible ? "#1a1a1a" : "#9ca3af",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  fontWeight: col.visible ? 600 : 400,
                }}
              >
                {formatKey(col.key)}
              </Typography>

              <Switch
                data-testid={`${testIdPrefix}-toggle-${idx}`}
                size="small"
                checked={col.visible}
                onChange={() => toggleVisible(idx)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#f58220" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: "#f58220",
                  },
                }}
              />
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
